import { actionProps } from "@/lib/controlAnalytics";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { buttonClass } from "@/components/common/controls";
import { captureError } from "@/lib/analytics";
import { isChunkLoadError, reloadForChunk } from "@/lib/chunkReload";

function sessionStore(): Storage | null {
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

type Props = {
    children: ReactNode;
    /** Changes when the route does, so a page that failed is tried again on the next. */
    resetKey?: string;
    /** For the tests: what a reload is. */
    reload?: () => void;
};

type State = { error: Error | null; reloading: boolean };

/**
 * What a page shows when it cannot render, rather than the whole app
 * going white: a failed load of a page's code after a deploy reloads
 * onto the new build once; anything else says so, with a way to try
 * again. Around the routes, so the sidebar stays and another page still
 * opens.
 */
export default class ErrorBoundary extends Component<Props, State> {
    state: State = { error: null, reloading: false };

    static getDerivedStateFromError(error: unknown): Partial<State> {
        return { error: error instanceof Error ? error : new Error(String(error)) };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        if (isChunkLoadError(error)) {
            const reloading = reloadForChunk(
                this.props.reload ?? (() => window.location.reload()),
                sessionStore(),
            );
            if (reloading) this.setState({ reloading: true });
            return;
        }
        console.error("A page could not render", error, info.componentStack);
        captureError(error);
    }

    componentDidUpdate(previous: Props): void {
        if (previous.resetKey !== this.props.resetKey && this.state.error) {
            this.setState({ error: null, reloading: false });
        }
    }

    render(): ReactNode {
        const { error, reloading } = this.state;
        if (!error) return this.props.children;
        if (reloading) return <div aria-hidden="true" className="min-h-[40vh]" />;
        const chunk = isChunkLoadError(error);
        return (
            <div
                role="alert"
                className="mx-auto my-10 max-w-[520px] rounded-card border border-line bg-card px-6 py-8 text-center shadow-card"
            >
                <h1 className="font-display text-[20px] font-semibold text-fg">
                    {chunk ? "Job Envoy was updated" : "This page could not be shown"}
                </h1>
                <p className="mt-2 text-[13px] text-fg-muted">
                    {chunk
                        ? "A newer version is out. Reload to get it."
                        : "Something went wrong drawing it. Your data is safe; try again, or open another page."}
                </p>
                <div className="mt-5 flex justify-center gap-2">
                    <button
                        {...actionProps("error_recovery_clicked", {
                            source: "ErrorBoundary",
                            mode: chunk ? "reload" : "retry",
                        })}
                        type="button"
                        className={buttonClass("primary", "md")}
                        onClick={() =>
                            chunk
                                ? (this.props.reload ?? (() => window.location.reload()))()
                                : this.setState({ error: null })
                        }
                    >
                        {chunk ? "Reload" : "Try again"}
                    </button>
                </div>
            </div>
        );
    }
}
